import { ExtractPropTypes } from 'vue'
import { PlusOutlined } from '@ant-design/icons-vue'
import { Tag, Modal, FormItemRest } from 'ant-design-vue/es'
import { useAppStore } from '@/store'
import { cloneDeep, isArray, isFunction } from 'lodash-es'
import GridTable from '@/components/table/grid-table.vue'
import { selectApi } from '@/api/selector'
import { uniqueFunc } from '@/utils/utils'
import { useMessage } from '@/hooks/message'
const userProps = {
  value: {
    type: Array
  },
  title: {
    type: String,
    default: '用户'
  },
  minCount: {
    type: Number,
  },
  maxCount: {
    type: Number,
  },
  okCallback: {
    type: Function,
  },
  hideTag: {
    type: Boolean,
    default: false
  },
  disableUser: {
    type: Array,
    default: []
  },
  isFilterValue: {
    type: Boolean,
    default: false,
  },
  getApi: {
    type: Function,
    default: selectApi.users
  },
  isBindPayroll: {
    type: Boolean,
    default: false
  },
  getListparams: {
    type: Object,
    default: () => ({})
  }
}
export type JobManagerSelectProps = Partial<ExtractPropTypes<typeof userProps>>

export default defineComponent({
  name: 'UserModalSelect',
  inheritAttrs: false,
  props: userProps,
  emits: ['update:value'],
  setup(props, ctx) {
    const { emit, expose } = ctx
    const { getReferenceItemsValueMap } = useAppStore()
    const users = ref<any>([])
    const visible = ref(false)
    const gridTableEl = ref<IGridTable.Expose>()
    watch(() => props.value, () => {
      if (isArray(props.value)) {
        users.value = cloneDeep(props.value)
      }
    }, {
      immediate: true,
      deep: true
    })
    const columns = computed(():IGridTable.ColumnProps[] => ([
      {
        title: '用户姓名',
        dataIndex: 'realname'
      },
      {
        title: '用户联系方式',
        dataIndex: 'mobile'
      },
      {
        title: '用户身份证',
        dataIndex: 'idcard'
      },
      {
        title: '员工姓名',
        dataIndex: 'employeeName'
      },
      {
        title: '员工联系方式',
        dataIndex: 'employeeMobile'
      },
      {
        title: '员工身份证',
        dataIndex: 'employeeIdcard'
      },
      {
        title: '是否绑定工资单',
        dataIndex: 'isBindPayroll',
        referenceKey: 'localYesNoEnum',
        isShow: () => props.isBindPayroll
      },
    ].filter(item => (item.isShow === undefined) || (isFunction(item.isShow) && item.isShow())) as IGridTable.ColumnProps[]))
    const tableAttrs = computed(():IGridTable.Props => ({
      api: {
        getList: async params => {
          const res = await props.getApi(Object.assign({}, params, props.getListparams))
          return res
        },
      },
      rowSelection: {
        onSelect: (record, selected) => {
          if (!selected && Array.isArray(users.value)) {
            users.value = users.value.filter(item => item.id != record?.id)
          } else {
            users.value = [...(users.value ?? []), record]
          }
        },
        onSelectAll: (selected, selectedRows, changeRows) => {
          if (!selected) {
            const changeKeys = changeRows.map(item => item.id)
            users.value = users.value?.filter(item => !changeKeys.includes(item.id))
          } else {
            users.value = Array.isArray(users.value) && [...users.value, ...changeRows]
          }
          console.log('onSelectAll', users.value)
        }
      },
      rowStriped: 'none',
      size: 'small',
      showTableHeader: false,
      selectable: true,
      columnSchemas: columns.value,
      searchSchemas: [
        {
          field: 'key',
          label: '关键字',
          component: 'Input',
          componentProps: {
            placeholder: '仅支持用户姓名、用户联系方式、用户身份证搜索'
          },
          span: 18,
        }
      ],
      getCheckboxProps: record => ({
        disabled: props.disableUser.includes(record.id)
      }),
      pagination: {
        pageSize: 10,
        onChange: () => {
          gridTableEl.value?.setSelectedKeys(unref(users)?.map(item => item.id))
          gridTableEl.value?.setSelectedRows(unref(users))
        }
      }
    }))

    if (props.value && props.value?.length > 0 && JSON.stringify(unref(users)) !== JSON.stringify(unref(props.value))) {
      users.value = cloneDeep(props.value)
    }


    const show = () => {
      visible.value = true
      nextTick(() => {
        gridTableEl.value?.setSelectedKeys(unref(users)?.map(item => item.id))
        gridTableEl.value?.setSelectedRows(unref(users))
      })
    }
    const updateValue = () => {
      // 去重
      users.value = uniqueFunc(users.value, 'id')
      let submitUsers = unref(users.value)
      // 过滤掉props.value已有的数据
      if (props.isFilterValue && Array.isArray(props.value)) {
        submitUsers = submitUsers?.filter(item => (!props.value?.map((item:any) => (item.id))?.includes(item.id)))
      }
      if (props.maxCount !== undefined && unref(submitUsers)?.length > props.maxCount) {
        const errMsg = `最多选择${props.maxCount}条数据！`
        useMessage.error(errMsg)
        throw new Error(errMsg)
      }
      emit('update:value', unref(submitUsers).length ? unref(submitUsers) : undefined)
    }
    const deleteUser = user => {
      users.value = unref(users).filter(item => item.id !== user.id)
      updateValue()
    }

    const handleOk = params => {
      if (isFunction(props.okCallback)) {
        // 去重
        users.value = uniqueFunc(users.value, 'id')
        let submitUsers = unref(users.value)
        // 过滤掉props.value已有的数据
        if (props.isFilterValue && Array.isArray(props.value)) {
          submitUsers = submitUsers?.filter(item => (!props.value?.map((item:any) => (item.id))?.includes(item.id)))
        }
        if (props.maxCount !== undefined && unref(submitUsers)?.length > props.maxCount) {
          const errMsg = `最多选择${props.maxCount}条数据！`
          useMessage.error(errMsg)
          throw new Error(errMsg)
        }
        if (props.minCount !== undefined && unref(submitUsers)?.length < props.minCount) {
          const errMsg = `请选择至少${props.minCount}条数据！`
          useMessage.error(errMsg)
          throw new Error(errMsg)
        }
        props.okCallback(unref(submitUsers).length ? unref(submitUsers) : [])
      } else {
        updateValue()
        visible.value = false
      }
    }
    const close = () => {
      users.value = cloneDeep(props.value ?? [])
      visible.value = false
    }
    expose({
      show,
      close
    })

    return () => <FormItemRest>
      {!props.hideTag && unref(users).map(user => (<Tag closable={true} key={user.id} onClose={() => deleteUser(user)}>{user.realname}</Tag>))}
      {!props.hideTag && <Tag onClick={show} class="cursor-pointer">
        选择{props.title}
        <PlusOutlined></PlusOutlined>
      </Tag>}
      <Modal zIndex={1001} visible={visible.value} onCancel={() => {
        close()
      }} width={1000} title={`${props.title}`} onOk={handleOk}>
        <GridTable ref={gridTableEl} {...tableAttrs.value}></GridTable>
      </Modal>
    </FormItemRest>
  }
})